import React, { useEffect, useState } from "react"
import { Box, Button, Chip, Grid, Stack } from "@mui/material";
import Typography from "@mui/material/Typography";
import {
    DataGrid,
    GridToolbarColumnsButton,
    GridToolbarContainer,
    GridToolbarDensitySelector,
    GridToolbarExport,
    GridToolbarFilterButton,
    GridToolbarQuickFilter, useGridApiRef
} from "@mui/x-data-grid";
import { useHistory } from "react-router-dom";
import { styled } from "@mui/material/styles";
import Paper from "@mui/material/Paper";
import ActionAlerts from "../../layout/ActionAlerts";

const ListItem = styled('li')(({ theme }) => ({
    margin: theme.spacing(0.5),
}));
function CustomToolbar() {
    return (
        <GridToolbarContainer title="Data" sx={{ justifyContent: 'flex-start' }}>
            <Typography variant='h5' align='center'>Open trades</Typography>
            <GridToolbarColumnsButton />
            <GridToolbarFilterButton />
            <GridToolbarDensitySelector />
            <GridToolbarExport
                excelOptions={{ allColumns: true }}
                csvOptions={{
                    fileName: 'open-trades',
                }}
            />
            <GridToolbarQuickFilter />
        </GridToolbarContainer>
    );
}

const OpenTrades = ({ user }) => {
    const history = useHistory();
    const [rows, setRows] = useState([{ symbol: '', users: [] }]);
    const [users, setUsers] = useState([{ id: '', name: '' }]);
    const [symbolUsersMap, setSymbolUsersMap] = useState(new Map());
    const [symbolSLMap, setSymbolSLMap] = useState(new Map());
    const apiRef = useGridApiRef();
    const [showNotification, setShowNotification] = useState(false);
    const [notificationMessages, setNotificationMessages] = useState([]);

    useEffect(() => {
        fetch(`/algo-trading/api/admin/users`, {
            method: 'GET',
            headers: {
                "Content-Type": "application/json",
                'Authorization': user.credential,
            }
        })
            .then(function (response) {
                response.text().then(r => {
                    if (response.ok) {
                        setUsers(JSON.parse(r))
                    } else {
                        console.log('FAIL', r)
                        history.push("/")
                    }
                })
            }, function (error) {
                console.log('ERROR', error.message)
                history.push("/")
            })
    }, [user])

    useEffect(() => {
        fetch(`/algo-trading/api/admin/open-trades`, {
            method: 'GET',
            headers: {
                "Content-Type": "application/json",
                'Authorization': user.credential,
            }
        })
            .then(function (response) {
                response.text().then(r => {
                    if (response.ok) {
                        let data = JSON.parse(r);
                        data.map((row) => {
                            row.users = row.users.split(",")
                        })
                        setRows(data)
                        const suMap = new Map()
                        data.map((row) => suMap.set(row.symbol, row.users))
                        setSymbolUsersMap(suMap)
                        const slMap = new Map()
                        data.map((row) => slMap.set(row.symbol, row?.logicalSL))
                        setSymbolSLMap(slMap)
                    } else {
                        console.log('FAIL', r)
                        history.push("/")
                    }
                })
            }, function (error) {
                console.log('ERROR', error.message)
                history.push("/")
            })
    }, [user]);

    const getUserName = (userId) => {
        let user = users.find((user) => user.id === userId);
        return user?.name;
    }
    function handleUserDelete(userId, symbol) {
        let users = symbolUsersMap?.get(symbol);
        let userRemoved = users?.filter(user => user !== userId);
        symbolUsersMap?.set(symbol, userRemoved);
        setSymbolUsersMap(symbolUsersMap);
        apiRef.current.updateRows([{ symbol: symbol, users: userRemoved }]);
    }

    const updateLogicalSL = (updatedRow, originalRow) => {
        if (updatedRow?.logicalSL === originalRow?.logicalSL) {
            return updatedRow;
        }
        const request = new Object();
        request.symbol = updatedRow?.symbol;
        request.userIds = updatedRow?.users;
        request.logicalSL = updatedRow?.logicalSL;
        console.log('request', JSON.stringify(request))
        fetch(`/algo-trading/api/admin/logical-sl`, {
            method: 'POST',
            body: JSON.stringify(request),
            headers: {
                "Content-Type": "application/json",
                'Authorization': user.credential,
            }
        })
            .then(function (response) {
                response.text().then(r => {
                    if (response.ok) {
                        setNotificationMessages(notificationMessages.concat(r));
                        setShowNotification(true);
                    } else {
                        console.log('FAIL', r)
                        let errorMessage = `Error updating logical SL Symbol ${updatedRow?.symbol} for users ${updatedRow?.userIds} error: ${r}`
                        setNotificationMessages([errorMessage]);
                        setShowNotification(true);
                    }
                })
            }, function (error) {
                console.log('ERROR', error.message)
                history.push("/")
            })
        return updatedRow;
    }
    function sellSymbol(symbol) {
        console.log('You clicked the sell button.' + symbol + ' ' + symbolUsersMap?.get(symbol));
        const request = new Object();
        request.symbol = symbol;
        request.userIds = symbolUsersMap?.get(symbol);

        fetch(`/algo-trading/api/admin/place-sell-orders`, {
            method: 'POST',
            body: JSON.stringify(request),
            headers: {
                "Content-Type": "application/json",
                'Authorization': user.credential,
            }
        })
            .then(function (response) {
                response.text().then(r => {
                    if (response.ok) {
                        let data = JSON.parse(r);
                        console.log('SUCCESS', data)

                        const messages = data.map((d) => d?.status
                            ? `Symbol ${d.symbol} sold for ${getUserName(d.userId)}`
                            : `Symbol ${d.symbol} is not sold for ${getUserName(d.userId)} due to ${d?.errorMessage}`)

                        setNotificationMessages(notificationMessages.concat(messages));
                        setShowNotification(true);
                    } else {
                        setNotificationMessages([JSON.parse(r)]);
                        setShowNotification(true);
                        console.log('FAIL', r)
                    }
                })
            }, function (error) {
                console.log('ERROR', error.message)
                setNotificationMessages([error.message]);
                setShowNotification(true);
            })
    }

    function calculateDistance(logicalSL, distanceFrom) {
        return logicalSL && distanceFrom > 0 ? (logicalSL - distanceFrom) * 100 / distanceFrom : 0;
    }

    const columns = [
        {
            field: 'symbol', headerName: 'Symbol', width: "150", type: 'string',
            renderCell: (params) => (
                <a href={"https://www.tradingview.com/chart/?symbol=NSE:" + params.value} target="_blank">
                    {params.value}
                </a>
            ),
        },
        {
            field: 'distanceFromLtp', headerName: 'LTP distance', width: "100", type: 'number', align: 'left', headerAlign: 'left',
            renderCell: (params) => {
                return params?.value ? (params?.value.toFixed(2) + '%') : ''
            },
        },
        {
            field: 'logicalSL', headerName: 'Logical SL', width: "100", type: 'number',
            editable: true, align: 'left', headerAlign: 'left',
            valueSetter: (params) => {
                let updatedLogicalSL = params?.value;
                let data = {
                    symbol: params.row?.symbol,
                    ltp: params.row?.ltp,
                    _52WHigh: params.row?._52WHigh,
                    logicalSL: parseFloat(updatedLogicalSL),
                    distanceFromLtp: calculateDistance(updatedLogicalSL, params.row?.ltp),
                    distanceFrom52WHigh: calculateDistance(params.row?.ltp, params.row?._52WHigh),
                    users: params.row?.users
                }
                return data;
            }
        },
        {
            field: 'ltp', headerName: 'LTP', width: "80", type: 'number', align: 'left', headerAlign: 'left',
        },
        {
            field: '_52WHigh', headerName: '52W High', width: "100", type: 'number', align: 'left', headerAlign: 'left',
        },
        {
            field: 'distanceFrom52WHigh', headerName: '52W distance', width: "100", type: 'number', align: 'left', headerAlign: 'left',
            renderCell: (params) => {
                return params?.value ? (params?.value.toFixed(2) + '%') : ''
            }
        },
        {
            field: 'users', headerName: 'Users', width: "700", type: 'string',
            renderCell: (params) => {
                let chips = params?.value?.map((userId) => {
                    const userName = getUserName(userId?.trim());
                    return (<Chip label={userName}
                        key={userId}
                        variant="outlined"
                        onDelete={() => handleUserDelete(userId, params?.row?.symbol)}
                        size={"medium"} />)
                })
                return (
                    <Paper
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            flexWrap: 'wrap',
                            listStyle: 'none',
                            p: 0.5,
                            m: 0,
                        }}
                        component="ul"
                    >
                        {chips}
                    </Paper>
                )
            },
        },
        {
            field: 'action', headerName: 'Action', width: '70',
            valueGetter: (params) => {
                return `${params.row?.symbol}`;
            },
            renderCell: (params) => (
                <Button color={"secondary"} variant={"outlined"} onClick={() => sellSymbol(params?.value)}>
                    <Typography variant="body2">Sell</Typography>
                </Button>
            ),
        }
    ]
    return (
        <>
            <Grid item xs={12}>
                <Typography variant="h4" gutterBottom component="div">
                    Open Trades
                </Typography>
            </Grid>
            <Grid item xs={12}>
                <Stack sx={{ width: '100%' }} spacing={2}>
                    {notificationMessages.length > 0
                        && notificationMessages.map((message, index) => (
                            <ActionAlerts message={message} key={index} toShow={true} />
                        ))}
                </Stack>
            </Grid>
            <Grid item xs={12} md={12}>
                <Box>
                    {users && rows && <DataGrid
                        getRowId={(row) => row?.symbol}
                        rows={rows}
                        columns={columns}
                        wordWrapEnabled={true}
                        processRowUpdate={(updatedRow, originalRow) => updateLogicalSL(updatedRow, originalRow)}
                        onProcessRowUpdateError={(error) => console.log('error', error)}
                        apiRef={apiRef}
                        getRowHeight={() => 'auto'}
                        initialState={{
                            sorting: {
                                sortModel: [{ field: 'symbol', sort: 'asc' }],
                            },
                            pagination: {
                                paginationModel: {
                                    pageSize: 25
                                }
                            }
                        }}
                        slots={{
                            toolbar: CustomToolbar
                        }}
                        sx={{
                            bgcolor: 'white'
                        }}
                    />}
                </Box>
            </Grid>
        </>
    )
}

export default OpenTrades